import type { ReportExport } from "@/api/types";

/**
 * A spreadsheet runs a cell that starts with one of these, and a narration or a supplier's name is
 * text somebody else typed. A leading apostrophe keeps it text. Figures are exempt, because a negative
 * amount starts with a minus.
 */
const RUNS_AS_A_FORMULA = /^[=+\-@\t\r]/;

function cell(value: string | null | undefined, numeric: boolean): string {
  const text = value ?? "";
  const safe = !numeric && RUNS_AS_A_FORMULA.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

/** A report as CSV: the title and period first, as in the workbook, then the table. */
export function toCsv(report: ReportExport): string {
  const line = (values: Array<string | null>) =>
    report.columns.map((column, index) => cell(values[index], column.numeric === true)).join(",");
  return [
    cell(report.title, false),
    ...(report.period ? [cell(report.period, false)] : []),
    "",
    report.columns.map((column) => cell(column.header, false)).join(","),
    ...report.rows.map(line),
    ...(report.totals ? [line(report.totals)] : []),
  ].join("\r\n");
}

/** With a byte-order mark, or Excel reads Devanagari as nonsense. */
export const toCsvBlob = (report: ReportExport) =>
  new Blob(["\uFEFF", toCsv(report)], { type: "text/csv;charset=utf-8" });
